import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import MultiLanguageDesignationModel from './MultiLanguageDesignationModel';
import * as AT from '../../components/container/attributes';
import {required} from '@vuelidate/validators';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';

import Country from './Country';
import {GeneralAccount} from './index';

import MultiLanguageField from './MultiLanguageField';

const max3Characters = (data) => {
  if (data) {
    return data.length <= 3;
  } else {
    return true;
  }
};

export default class TaxCode extends MultiLanguageDesignationModel {
  constructor({
    id,
    number,
    journalType = undefined,
    country = undefined,
    peppolId = undefined,
    active = true,
    percentage = undefined,
    taxAccount = undefined,
    counterPartTaxAccount = undefined,
    partiallyDeductible = undefined,
    partiallyDeductiblePercentage = undefined,
    partiallyDeductibleAccount = undefined,

    legalClauseFrBe = undefined,
    legalClauseDeBe = undefined,
    legalClauseNlBe = undefined,
    legalClauseFrLu = undefined,
    legalClauseDeLu = undefined,
    legalClauseFrFr = undefined,
    legalClauseDeDe = undefined,
    legalClauseNlNl = undefined,
    legalClauseEnGb = undefined,
    legalClauseEnIn = undefined,
    legalClauseMrIn = undefined,
    legalClauseHiIn = undefined,
    designationFrBe = undefined,
    designationDeBe = undefined,
    designationNlBe = undefined,
    designationFrLu = undefined,
    designationDeLu = undefined,
    designationFrFr = undefined,
    designationDeDe = undefined,
    designationNlNl = undefined,
    designationEnGb = undefined,
    designationEnIn = undefined,
    designationMrIn = undefined,
    designationHiIn = undefined,
  }) {
    super({
      id,
      number,
      designationFrBe,
      designationDeBe,
      designationNlBe,
      designationFrLu,
      designationDeLu,
      designationFrFr,
      designationDeDe,
      designationNlNl,
      designationEnGb,
      designationEnIn,
      designationMrIn,
      designationHiIn,
    });

    this.journalType = journalType
      ? HitOnlineEnums.getEnumValue('JOURNAL_TYPE', journalType, translate)
      : null;
    this.country = country ? new Country(country) : undefined;
    this.peppolId = peppolId;
    this.active = active;
    this.percentage = percentage;
    this.legalClauses = new MultiLanguageField({
      legalClauseFrBe,
      legalClauseDeBe,
      legalClauseNlBe,
      legalClauseFrLu,
      legalClauseDeLu,
      legalClauseFrFr,
      legalClauseDeDe,
      legalClauseNlNl,
      legalClauseEnGb,
      legalClauseEnIn,
      legalClauseMrIn,
      legalClauseHiIn,
    });
    this.legalClause = this.legalClauses.value;

    this.taxAccount = taxAccount ? new GeneralAccount(taxAccount) : undefined;
    this.counterPartTaxAccount = counterPartTaxAccount
      ? new GeneralAccount(counterPartTaxAccount)
      : undefined;
    this.partiallyDeductible = partiallyDeductible;
    this.partiallyDeductiblePercentage = partiallyDeductiblePercentage;
    this.partiallyDeductibleAccount = partiallyDeductibleAccount
      ? new GeneralAccount(partiallyDeductibleAccount)
      : undefined;
  }

  /**
   * To gain in space in the different lists, we only use no as designation (if defined)
   */
  get fullDesignation() {
    return this.number || this.designations.value;
  }

  get entityDesignation() {
    return HitUtils.createStandardDesignation(this);
  }

  get fastModeBrowserDesignation() {
    return HitUtils.createStandardDesignation({
      number: this.number,
      designation: this.designationForLocale(this.displayLocale),
    });
  }

  static get joinAttrsForDesignation() {
    return ['number', ...this.localeAttributeNames('designation')]
      .map((key) => this.allAttributes.find((attr) => attr.name === key))
      .filter((attr) => attr !== undefined);
  }

  static get fkName() {
    return 'tax_code_id';
  }
  static get apiRoute() {
    return 'tax_code';
  }
  static get detailPageRoute() {
    return 'taxCodeDetail';
  }
  static get listPageRoute() {
    return 'taxCodeList';
  }
  get fkName() {
    return TaxCode.fkName;
  }
  get apiRoute() {
    return TaxCode.apiRoute;
  }
  get listPageRoute() {
    return TaxCode.listPageRoute;
  }
  get detailPageRoute() {
    return TaxCode.detailPageRoute;
  }
  static get createInitColumns() {
    return {
      [this.attributeCompanyLocale('designation', true)]: '',
      active: true,
      percentage: 0.0,
    };
  }

  static get attrsToStoreInForm() {
    return [
      'number',
      this.attributeUserLocale('designation'),
      'percentage',
      'active',
    ];
  }
  get attrsToStoreInForm() {
    return TaxCode.attrsToStoreInForm;
  }

  static get joinAttrsKey() {
    return [
      'number',
      ...this.localeAttributeNames('designation'),
      'percentage',
      'taxAccount',
      'counterPartTaxAccount',
      'partiallyDeductible',
      'partiallyDeductiblePercentage',
      'partiallyDeductibleAccount',
    ];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      this.attributeUserLocale('designation'),
      this.attributeCompanyLocale('designation'),
      'journalType',
      'country',
      'active',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'number',
      ...this.localeAttributeNames('designation'),
      'percentage',
      'journalType',
    ]);
  }

  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '1fr'}),
      new AT.EnumerationAttribute({
        name: 'journalType',
        column: 'journal_type',
        enumType: 'JOURNAL_TYPE',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-app.journal.journal-type',
          maxWidth: '1fr',
        },
      }),
      new AT.FkCountryAttribute({
        name: 'country',
        joinPath: ['country!fk_tax_code_country_country'],
        tableProperties: {
          header: 'hit-base.common.country',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'peppolId',
        validations: {max3Characters},
        column: 'peppol_id',
        tableProperties: {
          header: 'hit-app.common.peppol-id',
          maxWidth: '1fr',
        },
      }),
      this.ACTIVE_ATTRIBUTE(),
      new AT.DecimalAttribute({
        name: 'percentage',
        column: 'percentage',
        validations: {required},
        decimalPrecision: 2,
        creationValue: 0.0,
        tableProperties: {
          header: 'hit-app.tax-code.percentage',
          maxWidth: '1fr',
        },
      }),
      new AT.FkAccountAttribute({
        name: 'taxAccount',
        column: 'tax_account_id',
        joinPath: ['account!fk_tax_code_tax_account_id_account'],
        fkToUpdate: 'tax_account_id',
        tableProperties: {
          header: 'hit-app.common.tax-account',
          maxWidth: '1fr',
        },
      }),
      new AT.FkAccountAttribute({
        name: 'counterPartTaxAccount',
        column: 'counter_part_tax_account_id',
        joinPath: ['account!fk_tax_code_counter_part_tax_account_id_account'],
        fkToUpdate: 'counter_part_tax_account_id',
        tableProperties: {
          header: 'hit-app.common.counter-part-tax-account',
          maxWidth: '1fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'partiallyDeductible',
        column: 'partially_deductible',
        tableProperties: {
          header: 'hit-app.common.partially-deductible',
        },
      }),
      new AT.DecimalAttribute({
        name: 'partiallyDeductiblePercentage',
        column: 'partially_deductible_percentage',
        validations: {required},
        decimalPrecision: 2,
        decimalDefaultValue: 0.0,
        tableProperties: {
          header: 'hit-app.partially-deductible-percentage',
          maxWidth: '1fr',
        },
      }),
      new AT.FkAccountAttribute({
        name: 'partiallyDeductibleAccount',
        column: 'partially_deductible_account_id',
        joinPath: [
          'account!fk_tax_code_partially_deductible_account_id_account',
        ],
        fkToUpdate: 'partially_deductible_account_id',
        tableProperties: {
          header: 'hit-app.common.partially-deductible-account-id',
          maxWidth: '1fr',
        },
      }),
    ]
      .concat(
        this.generateMultiLanguageField({
          key: 'designation',
          attributeClass: AT.StringAttribute,
          header: 'hit-base.common.designation',
        }),
      )
      .concat(
        this.generateMultiLanguageField({
          key: 'legalClause',
          attributeClass: AT.StringAttribute,
          header: 'hit-app.tax-code.legal-clause',
          column: 'legal_clause',
        }),
      );
  }
}
